import type {
  GameRowCollection,
  IndexGameQuery,
  StateRowTransportCollection,
} from '../../backend.d'
import {
  IndexGameQuerySchema,
} from '../../backend_schemas'
import { createResource, type Accessor } from 'solid-js'
import { safeParse } from 'valibot'
import { backendAxios } from '../../axios'
import { getStateDiskByType } from '../../lib/core/disk/stateDiskResolver'
import {
  EXPORT_DISK_PLACEHOLDER_ERROR,
  StateMapDisk,
  WORKSPACE_DISK_PLACEHOLDER_ERROR,
} from '../../lib/core/state/state_map_disk.enum'
import { StateType } from '../../lib/core/state/state_type.enum'
import { isOfflineOnly } from '../../lib/core/isOfflineOnly'
import { hasIndexedDB } from '../../lib/core/Storage/IndexedDB/hasIndexedDB'
import { decodePersistedStateData } from '../../lib/core/state/persisted_state'

export interface ExtendedIndexGameQuery extends IndexGameQuery {
  disk?: StateMapDisk
}

async function fetchGameCollectionByQuery(inputParams: ExtendedIndexGameQuery): Promise<GameRowCollection> {
  const disk = inputParams.disk
  const result = safeParse(IndexGameQuerySchema, inputParams)

  if (result.success) {
    if (!disk) {
      throw new Error('Missing disk')
    }
    const params = result.output
    let collection: StateRowTransportCollection = []

    if (disk === StateMapDisk.IndexedDB) {
      if (!hasIndexedDB()) {
        throw new Error('IndexedDB requested but not available')
      }
      collection = await getStateDiskByType(disk, StateType.Game).list()
    } else if (disk === StateMapDisk.Memory) {
      collection = await getStateDiskByType(disk, StateType.Game).list()
    } else if (disk === StateMapDisk.Cloud && !isOfflineOnly && import.meta.env.VITE_HONO_URL) {
      try {
        const response = await backendAxios.get(`/st1/game`, { params })
        collection = response.data as StateRowTransportCollection
      } catch (e) {
        console.error('Failed to fetch from cloud', e)
      }
    } else if (disk === StateMapDisk.Export) {
      throw new Error(EXPORT_DISK_PLACEHOLDER_ERROR)
    } else if (disk === StateMapDisk.Workspace) {
      throw new Error(WORKSPACE_DISK_PLACEHOLDER_ERROR)
    } else {
      throw new Error('Invalid disk: ' + disk)
    }

    return collection.map((row) => ({
      ...row,
      data: decodePersistedStateData(row.data, disk === StateMapDisk.Memory ? StateMapDisk.Memory : StateMapDisk.IndexedDB),
    })) as GameRowCollection
  }
  return []
}

export default function createGameCollectionResource(
  getInputQuery: Accessor<Partial<ExtendedIndexGameQuery>>
) {
  const resource = createResource(getInputQuery, fetchGameCollectionByQuery)
  return resource
}
